import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowUpDown,
  Bookmark,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleHelp,
  Download,
  Hourglass,
  Info,
  Lock,
  Package,
  RotateCcw,
  RotateCw,
  SlidersHorizontal,
  Star,
  Users,
  X,
} from 'lucide-react';
import AgedInventoryApi, { AgingTierKey, DropdownOption } from '../../../services/reports/AgedInventory.service';
import { SAVED_VIEW_NAME_MAX_LENGTH, SavedViewVisibility } from '../../../services/reports/SavedViews.service';
import { useSavedViews } from '../../../hooks/useSavedViews';
import { normalizeColumnOrder, useDraggableColumnOrder } from '../../../hooks/useDraggableColumnOrder';
import { COLUMN_RESIZE_HANDLE_CLASS, useResizableColumns } from '../../../hooks/useResizableColumns';
import { pinColumn, unpinColumn, usePinnedColumns } from '../../../hooks/columnPinning';
import { usePendingFilterSelection } from '../../../hooks/usePendingFilterSelection';
import AuthProvider from '../../../providers/AuthProvider';
import { Skeleton } from '../../../tailwindComponents/ui/skeleton';
import { ColumnHeaderMenu, ColumnSortDirection, SORT_DIRECTION_LABELS } from '../ColumnHeaderMenu.component';
import { downloadCsv } from '../downloadCsv';
import { matchesClassQuery, toLocalIsoDate } from '../topSellers/TopSellersReport.component';
import { InfoTooltip } from './InfoTooltip.component';
import {
  DateRangeFilter,
  FilterChip,
  FilterChips,
  FilterLabel,
  FilterOption,
  MultiSelectFilter,
  SearchInput,
  SegmentedControl,
  SingleSelectFilter,
  ToggleFilter,
  selectedNames,
} from './AgedInventoryFilters.component';
import {
  AGE_THRESHOLD_OPTIONS,
  AGED_INVENTORY_COLUMNS,
  COLUMNS_POPOVER_NOTE,
  AGED_INVENTORY_STORAGE_PREFIX,
  AGING_TIERS,
  AgedInventoryColumnConfig,
  AgedInventoryColumnKey,
  AgedInventoryFilterState,
  AgedInventoryGroup,
  AgedInventoryHierarchy,
  AgedInventorySku,
  AgedInventorySortKey,
  AgedInventoryStyle,
  AgedInventoryTotals,
  AgedInventoryViewPayload,
  CUSTOM_DATE_RANGE,
  DATE_RANGE_PRESETS,
  DEFAULT_COLUMN_ORDER,
  DEFAULT_COLUMN_WIDTHS,
  DEFAULT_FILTERS,
  DEFAULT_SORT,
  HIERARCHY_OPTIONS,
  METRIC_COLUMN_WIDTH,
  PAGE_SIZE,
  PRODUCT_COLUMN_DESCRIPTION,
  PRODUCT_COLUMN_KEY,
  PRODUCT_COLUMN_LABEL,
  SEARCH_FIELD_OPTIONS,
  SearchField,
  SkuGrain,
  SortConfig,
  ageRangeLabel,
  ageThresholdLabel,
  agingTierByKey,
  agingTierLabel,
  buildAgedInventoryCsv,
  cellText,
  columnByKey,
  computeTotals,
  formatDate,
  formatDateRange,
  getAgingTier,
  getColumnOrderStorageKey,
  getColumnWidthsStorageKey,
  getPinnedColumnsStorageKey,
  groupStyles,
  groupValueOf,
  isColumnShown,
  mapStyleRow,
  matchesSearch,
  normalizeViewPayload,
  resolveDateRange,
  sortKeyLabel,
  sortStyles,
  sortTypeOf,
  totalsCellText,
  variantCountLabel,
} from './agedInventory.logic';

export interface AgedInventoryReportProps {
  clientId: string | number;
  // Accepted for the report registry's common signature; ages are as of the latest snapshot.
  epochMonth?: number;
  onClose?: () => void;
}

const ALIGN_CLASS = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;
const JUSTIFY_CLASS = { left: 'justify-start', right: 'justify-end', center: 'justify-center' } as const;
const SKELETON_ROW_COUNT = 8;
const SEARCH_DEBOUNCE_MS = 250;
const STICKY_CELL_CLASS = 'sticky left-0 z-10 border-r border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]';
const HEADER_BUTTON_CLASS =
  'h-[34px] px-[14px] bg-white border border-[#D0D5DD] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs flex items-center gap-1.5 transition-colors leading-[21px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';
const ICON_BUTTON_CLASS =
  'h-[34px] w-[34px] bg-white border border-[#D0D5DD] rounded-[9px] text-[#667085] hover:text-[#09090B] hover:bg-[#F9FAFB] shadow-2xs flex items-center justify-center transition-colors cursor-pointer';

const INFO = {
  dateRange: 'Sales window for Units Sold, Sell-Through % and Zero-sales only. Age and on-hand figures are always as of the latest inventory snapshot.',
  ageThreshold: 'Only styles at least this many days old, by their oldest SKU.',
  agingTier: 'Only styles whose oldest SKU falls in this aging tier.',
  hierarchy: 'Group styles under a category, class or vendor, with subtotals for each group.',
  showSkus: 'Product lists one row per style. SKU adds each size / color under its style.',
  zeroSales: 'Only styles with no units sold in the selected date range.',
  excludeCore: 'Hide core and continuous-reorder items, which are meant to stay in stock.',
  location: 'Stores to include. Units on hand and units sold count only these stores.',
  category: 'Merchandise categories to include.',
  class: 'Plan classes to include.',
  vendor: "Vendors to include, from the client's full catalog.",
  search: 'Find styles by product name, or by SKU.',
  summary: 'Styles matching the filters and search. Sizes/colors counts every SKU of those styles.',
} as const;

const SHOW_SKUS_OPTIONS = [
  { value: 'product', label: 'Product' },
  { value: 'sku', label: 'SKU' },
] as const;

type EntityFilter = 'location' | 'class' | 'vendor' | 'category';

/** "Age (days), oldest first" / "Vendor (A to Z)" -- the summary line's sort wording, after the column name. */
export function sortDirectionLabel(sort: SortConfig): string {
  if (sort.key === 'days_on_hand' || sort.key === 'aging_tier') return sort.direction === 'desc' ? 'oldest first' : 'newest first';
  return SORT_DIRECTION_LABELS[sortTypeOf(sort.key)][sort.direction];
}

const toClassOption = (option: DropdownOption): FilterOption => ({
  code: option.code,
  name: option.code && option.code !== option.name ? `${option.code} - ${option.name}` : option.name,
});

// "1 style", "35 styles".
const stylesLabel = (count: number): string => `${count} ${count === 1 ? 'style' : 'styles'}`;

const toFilterOption = (option: DropdownOption): FilterOption => ({ code: option.code, name: option.name || option.code });

const allColumnsVisible = (): Record<AgedInventoryColumnKey, boolean> =>
  Object.fromEntries(DEFAULT_COLUMN_ORDER.map((key) => [key, true])) as Record<AgedInventoryColumnKey, boolean>;

const TierBadge: React.FC<{ days: number | null; children: React.ReactNode }> = ({ days, children }) => {
  const tier = getAgingTier(days);
  if (!tier) return null;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-bold whitespace-nowrap ${tier.badgeClass}`}>{children}</span>
  );
};

const HeaderButton: React.FC<{
  label: string;
  description: string;
  isActive: boolean;
  direction: ColumnSortDirection;
  isMenuOpen: boolean;
  align: 'left' | 'right' | 'center';
  tooltipAlign: 'left' | 'right';
  onToggleMenu: () => void;
  menu: React.ReactNode;
}> = ({ label, description, isActive, direction, isMenuOpen, align, tooltipAlign, onToggleMenu, menu }) => (
  <div className={`relative flex items-center ${JUSTIFY_CLASS[align]} gap-0.5 w-full`} data-column-menu>
    <InfoTooltip text={description} position="bottom" align={tooltipAlign} disabled={isMenuOpen} />
    <button
      type="button"
      onClick={onToggleMenu}
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
      className={`flex items-center gap-1 hover:text-[#09090B] transition-colors cursor-pointer ${ALIGN_CLASS[align]} ${
        isActive || isMenuOpen ? 'text-[#09090B]' : ''
      }`}
    >
      <span className="whitespace-normal break-words leading-tight">{label}</span>
      {isActive ? (
        direction === 'asc' ? (
          <ChevronUp className="w-3 h-3 shrink-0" />
        ) : (
          <ChevronDown className="w-3 h-3 shrink-0" />
        )
      ) : (
        <ArrowUpDown className="w-3 h-3 text-[#94A3B8] shrink-0" />
      )}
    </button>
    {isMenuOpen && menu}
  </div>
);

export const AgedInventoryReport: React.FC<AgedInventoryReportProps> = ({ clientId, onClose }) => {
  const auth = AuthProvider.useAuth() as { user?: { id?: string | number } | null } | null;
  const userId = auth?.user?.id ?? null;

  // --- Filters & layout (applied) ---
  const [filters, setFilters] = useState<AgedInventoryFilterState>(DEFAULT_FILTERS);
  const updateFilters = (changes: Partial<AgedInventoryFilterState>) => setFilters((prev) => ({ ...prev, ...changes }));
  const [hierarchy, setHierarchy] = useState<AgedInventoryHierarchy>('total');
  const [skuGrain, setSkuGrain] = useState<SkuGrain>('product');
  const [sort, setSort] = useState<SortConfig>(DEFAULT_SORT);

  const [searchField, setSearchField] = useState<SearchField>('product_name');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { from, to } = resolveDateRange(filters.dateRange, filters.customStartDate, filters.customEndDate);

  // --- Options ---
  const [locationOptions, setLocationOptions] = useState<FilterOption[]>([]);
  const [classOptions, setClassOptions] = useState<FilterOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<FilterOption[]>([]);
  const [vendorOptions, setVendorOptions] = useState<FilterOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    AgedInventoryApi.getDropdownOptions(clientId)
      .then((opts) => {
        if (cancelled) return;
        setLocationOptions(opts.locations.map(toFilterOption));
        setClassOptions(opts.classes.map(toClassOption));
        setCategoryOptions(opts.categories.map(toFilterOption));
      })
      .catch(() => {
        if (cancelled) return;
        setLocationOptions([]);
        setClassOptions([]);
        setCategoryOptions([]);
      });
    AgedInventoryApi.getVendorOptions(clientId)
      .then((vendors) => {
        if (!cancelled) setVendorOptions(vendors.map((vendor) => ({ code: vendor, name: vendor })));
      })
      .catch(() => {
        if (!cancelled) setVendorOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // --- Data ---
  const [styles, setStyles] = useState<AgedInventoryStyle[]>([]);
  const [asOfDate, setAsOfDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    AgedInventoryApi.getAgedInventory({
      clientId,
      from,
      to,
      locationCodes: filters.locations,
      excludeLocations: filters.excludeLocations,
      classCodes: filters.classes,
      excludeClasses: filters.excludeClasses,
      vendorIds: filters.vendors,
      excludeVendors: filters.excludeVendors,
      categoryIds: filters.categories,
      excludeCategories: filters.excludeCategories,
      ageThreshold: filters.ageThreshold,
      agingTier: filters.agingTier,
      zeroSalesOnly: filters.zeroSalesOnly,
      excludeCore: filters.excludeCore,
    })
      .then((res) => {
        if (cancelled) return;
        setStyles((res?.rows ?? []).map(mapStyleRow));
        setAsOfDate(res?.as_of_date ?? null);
        // A failed query can still answer 200; say so rather than show a quiet empty table.
        if (res?.query_error) setLoadError(res.query_error);
      })
      .catch(() => {
        if (cancelled) return;
        setStyles([]);
        setAsOfDate(null);
        setLoadError('Failed to load Aged Inventory data. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, filters, from, to]);

  // --- Columns ---
  const [visibleColumns, setVisibleColumns] = useState<Record<AgedInventoryColumnKey, boolean>>(allColumnsVisible);
  const { columnOrder, updateColumnOrder, resetColumnOrder, getDragProps, dragClass } = useDraggableColumnOrder(
    DEFAULT_COLUMN_ORDER,
    getColumnOrderStorageKey(userId, clientId)
  );
  const { pinnedColumns, isPinned, setPinnedColumns, clearPinnedColumns } = usePinnedColumns(
    getPinnedColumnsStorageKey(userId, clientId),
    DEFAULT_COLUMN_ORDER
  );
  const { widthOf, isResizing, getResizeHandleProps, resetColumnWidths } = useResizableColumns(
    getColumnWidthsStorageKey(userId, clientId),
    DEFAULT_COLUMN_WIDTHS,
    METRIC_COLUMN_WIDTH
  );

  const visibleOrderedColumns: AgedInventoryColumnConfig[] = columnOrder
    .map((key) => columnByKey(key))
    .filter((col): col is AgedInventoryColumnConfig => col !== undefined && isColumnShown(col, skuGrain, visibleColumns));

  const togglePinnedColumn = (key: AgedInventoryColumnKey) => {
    if (isPinned(key)) {
      const remaining = pinnedColumns.filter((k) => k !== key);
      setPinnedColumns(remaining);
      updateColumnOrder(unpinColumn(columnOrder, key, remaining, DEFAULT_COLUMN_ORDER));
    } else {
      updateColumnOrder(pinColumn(columnOrder, key, pinnedColumns));
      setPinnedColumns([...pinnedColumns, key]);
    }
  };

  // Pinned columns can't be dragged or dropped on, so a drag never moves a column ahead of them.
  const getColumnDragProps = (key: AgedInventoryColumnKey) => (isPinned(key) ? { draggable: false } : getDragProps(key, 'x'));

  // --- Popovers & menus ---
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const togglePopover = (name: string) => setActivePopover((open) => (open === name ? null : name));
  const closePopover = () => setActivePopover(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement | null)?.closest('[data-popover-container]')) setActivePopover(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [activeMenuColumn, setActiveMenuColumn] = useState<string | null>(null);
  const toggleColumnMenu = (key: string) => setActiveMenuColumn((open) => (open === key ? null : key));
  const closeColumnMenu = () => setActiveMenuColumn(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement | null)?.closest('[data-column-menu]')) setActiveMenuColumn(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The applied entity filters change once per popover session, not per checkbox.
  const locationDraft = usePendingFilterSelection(
    { codes: filters.locations, exclude: filters.excludeLocations },
    activePopover === 'location',
    (next) => updateFilters({ locations: next.codes, excludeLocations: next.exclude })
  );
  const classDraft = usePendingFilterSelection(
    { codes: filters.classes, exclude: filters.excludeClasses },
    activePopover === 'class',
    (next) => updateFilters({ classes: next.codes, excludeClasses: next.exclude })
  );
  const vendorDraft = usePendingFilterSelection(
    { codes: filters.vendors, exclude: filters.excludeVendors },
    activePopover === 'vendor',
    (next) => updateFilters({ vendors: next.codes, excludeVendors: next.exclude })
  );
  const categoryDraft = usePendingFilterSelection(
    { codes: filters.categories, exclude: filters.excludeCategories },
    activePopover === 'category',
    (next) => updateFilters({ categories: next.codes, excludeCategories: next.exclude })
  );

  // --- Rows: search -> sort -> group -> page ---
  const sortedStyles = useMemo(
    () => sortStyles(styles.filter((style) => matchesSearch(style, debouncedSearch, searchField)), sort),
    [styles, debouncedSearch, searchField, sort]
  );
  const groups = useMemo(() => groupStyles(sortedStyles, hierarchy), [sortedStyles, hierarchy]);
  // Styles in on-screen order: by group (leading group first), then by the sort within each.
  const orderedStyles = useMemo(() => (hierarchy === 'total' ? sortedStyles : groups.flatMap((group) => group.styles)), [hierarchy, sortedStyles, groups]);
  const totals = useMemo(() => computeTotals(orderedStyles), [orderedStyles]);
  const skuCount = orderedStyles.reduce((count, style) => count + Math.max(style.skus.length, 1), 0);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => setVisibleCount(PAGE_SIZE), [styles, debouncedSearch, searchField, sort, hierarchy]);
  const shownStyles = orderedStyles.slice(0, visibleCount);
  const shownIds = new Set(shownStyles.map((style) => style.id));
  const remaining = orderedStyles.length - shownStyles.length;

  // At SKU grain every style opens on its SKUs; a chevron collapses one.
  const [collapsedStyles, setCollapsedStyles] = useState<Record<string, boolean>>({});
  const toggleCollapsed = (id: string) => setCollapsedStyles((prev) => ({ ...prev, [id]: !prev[id] }));
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // --- Saved views ---
  const { views: savedViews, saveView, deleteView } = useSavedViews<AgedInventoryViewPayload>({
    reportKey: 'aged_inventory',
    clientId,
    userId,
    storagePrefix: AGED_INVENTORY_STORAGE_PREFIX,
    // Views have only ever been stored on the server for this report.
    readLocalViews: () => [],
  });
  const [newViewName, setNewViewName] = useState('');
  const [newViewVisibility, setNewViewVisibility] = useState<SavedViewVisibility>('only_me');
  const [isSavingView, setIsSavingView] = useState(false);
  const [savedViewError, setSavedViewError] = useState<string | null>(null);

  const handleApplySavedView = (rawPayload: unknown) => {
    const payload = normalizeViewPayload(rawPayload);
    setFilters(payload.filters);
    setHierarchy(payload.hierarchy);
    setSkuGrain(payload.skuGrain);
    setSort(payload.sort);
    setVisibleColumns(
      Object.fromEntries(DEFAULT_COLUMN_ORDER.map((key) => [key, payload.visibleColumns.includes(key)])) as Record<AgedInventoryColumnKey, boolean>
    );
    if (payload.columnOrder.length > 0) {
      // The saved order defines the layout; pins from this session would no longer hold.
      updateColumnOrder(normalizeColumnOrder(payload.columnOrder, DEFAULT_COLUMN_ORDER));
      clearPinnedColumns();
    }
    setActivePopover(null);
  };

  const handleSaveCurrentView = async () => {
    const name = newViewName.trim();
    if (!name || isSavingView) return;
    const payload: AgedInventoryViewPayload = {
      filters,
      hierarchy,
      skuGrain,
      sort,
      // Saved independent of grain, so a SKU-only column hidden at product grain stays wanted.
      visibleColumns: DEFAULT_COLUMN_ORDER.filter((key) => visibleColumns[key]),
      columnOrder,
    };
    setIsSavingView(true);
    setSavedViewError(null);
    try {
      await saveView({ name, visibility: newViewVisibility, payload });
      setNewViewName('');
      setNewViewVisibility('only_me');
    } catch (error) {
      console.error('Failed to save aged inventory view', error);
      setSavedViewError('Could not save this view. Please try again.');
    } finally {
      setIsSavingView(false);
    }
  };

  const handleDeleteSavedView = async (id: number, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavedViewError(null);
    try {
      await deleteView(id);
    } catch (error) {
      console.error('Failed to delete aged inventory view', error);
      setSavedViewError(`Could not delete "${name}". Please try again.`);
    }
  };

  // --- Reset / chips ---
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery('');
  };

  const handleResetAll = () => {
    clearFilters();
    setHierarchy('total');
    setSkuGrain('product');
    setSearchField('product_name');
    setSort(DEFAULT_SORT);
    setVisibleColumns(allColumnsVisible());
    resetColumnOrder();
    clearPinnedColumns();
    resetColumnWidths();
    setCollapsedStyles({});
    setCollapsedGroups({});
    setActivePopover(null);
  };

  const entityChip = (
    key: EntityFilter,
    label: string,
    codes: string[],
    exclude: boolean,
    options: FilterOption[],
    pluralNoun: string,
    clear: Partial<AgedInventoryFilterState>
  ): FilterChip | null =>
    codes.length === 0
      ? null
      : {
          key,
          label: `${label}: ${exclude ? 'not ' : ''}${selectedNames(codes, options).join(', ')}`,
          onRemove: () => updateFilters(clear),
        };

  const chips: FilterChip[] = [
    filters.dateRange !== DEFAULT_FILTERS.dateRange
      ? {
          key: 'date',
          label: `Date range: ${filters.dateRange === CUSTOM_DATE_RANGE ? formatDateRange(from, to) : filters.dateRange}`,
          onRemove: () => updateFilters({ dateRange: DEFAULT_FILTERS.dateRange, customStartDate: '', customEndDate: '' }),
        }
      : null,
    entityChip('location', 'Location', filters.locations, filters.excludeLocations, locationOptions, 'locations', { locations: [], excludeLocations: false }),
    entityChip('category', 'Category', filters.categories, filters.excludeCategories, categoryOptions, 'categories', { categories: [], excludeCategories: false }),
    entityChip('class', 'Class', filters.classes, filters.excludeClasses, classOptions, 'classes', { classes: [], excludeClasses: false }),
    entityChip('vendor', 'Vendor', filters.vendors, filters.excludeVendors, vendorOptions, 'vendors', { vendors: [], excludeVendors: false }),
    filters.ageThreshold !== null
      ? { key: 'age', label: `Age: ${ageThresholdLabel(filters.ageThreshold)}`, onRemove: () => updateFilters({ ageThreshold: null }) }
      : null,
    filters.agingTier !== null
      ? { key: 'tier', label: `Tier: ${agingTierByKey(filters.agingTier)?.name ?? filters.agingTier}`, onRemove: () => updateFilters({ agingTier: null }) }
      : null,
    filters.zeroSalesOnly ? { key: 'zero', label: 'Zero-sales only', onRemove: () => updateFilters({ zeroSalesOnly: false }) } : null,
    filters.excludeCore ? { key: 'core', label: 'Excluding core/reorder', onRemove: () => updateFilters({ excludeCore: false }) } : null,
    debouncedSearch ? { key: 'search', label: `Search: "${debouncedSearch}"`, onRemove: () => setSearchQuery('') } : null,
  ].filter((chip): chip is FilterChip => chip !== null);

  // --- Export: every style the filters return, not just the page shown ---
  const hierarchyConfig = HIERARCHY_OPTIONS.find((opt) => opt.key === hierarchy) ?? HIERARCHY_OPTIONS[0];
  const handleExportCSV = () => {
    const csv = buildAgedInventoryCsv(orderedStyles, {
      columns: visibleOrderedColumns,
      includeSkus: skuGrain === 'sku',
      groupHeader: hierarchy === 'total' ? null : hierarchyConfig.groupNoun,
      groupOf: (style) => groupValueOf(style, hierarchy),
    });
    downloadCsv(csv, `aged-inventory-${toLocalIsoDate(new Date())}-${hierarchy}-${skuGrain}.csv`);
  };

  // --- Cells ---
  const cellStyle = (key: string) => ({ width: widthOf(key), minWidth: widthOf(key) });

  const renderMetricCell = (col: AgedInventoryColumnConfig, row: AgedInventoryStyle | AgedInventorySku, isSku: boolean) => {
    // Nothing sold in the date range is flagged in red.
    const textColor =
      col.key === 'units_sold' && row.units_sold === 0 ? 'text-[#B42318] font-bold' : isSku ? 'text-[#64748B] font-medium' : 'text-[#344054] font-medium';
    const base = `px-4 ${isSku ? 'py-2 text-[12px]' : 'py-2.5 text-[13px]'} ${textColor} ${ALIGN_CLASS[col.align]} whitespace-nowrap`;

    if (col.key === 'days_on_hand') {
      const range = isSku ? null : ageRangeLabel(row as AgedInventoryStyle);
      return (
        <td key={col.key} className={base} style={cellStyle(col.key)}>
          {row.days_on_hand !== null && (
            <div className="flex flex-col items-center gap-0.5">
              <TierBadge days={row.days_on_hand}>{row.days_on_hand}</TierBadge>
              {range && <span className="text-[11px] text-[#98A2B3]">{range}</span>}
            </div>
          )}
        </td>
      );
    }
    if (col.key === 'aging_tier') {
      return (
        <td key={col.key} className={base} style={cellStyle(col.key)}>
          <TierBadge days={row.days_on_hand}>{getAgingTier(row.days_on_hand)?.name}</TierBadge>
        </td>
      );
    }
    // Vendor and class belong to the style; SKU rows leave them blank.
    const text = isSku && (col.key === 'vendor' || col.key === 'class_name') ? '' : cellText(row, col.key);
    return (
      <td key={col.key} className={base} style={cellStyle(col.key)}>
        {text}
      </td>
    );
  };

  const renderTotalsCells = (rowTotals: AgedInventoryTotals, emphasis: string) =>
    visibleOrderedColumns.map((col) => (
      <td key={col.key} className={`px-4 py-2.5 text-[13px] ${emphasis} ${ALIGN_CLASS[col.align]} whitespace-nowrap`} style={cellStyle(col.key)}>
        {totalsCellText(rowTotals, col.key)}
      </td>
    ));

  const renderStyleRows = (style: AgedInventoryStyle) => {
    const showSkus = skuGrain === 'sku' && style.skus.length > 0;
    const isOpen = showSkus && !collapsedStyles[style.id];
    const countLabel = variantCountLabel(style);
    return (
      <React.Fragment key={style.id}>
        <tr className={`group bg-white hover:bg-[#F9FAFB] transition-colors ${showSkus ? 'cursor-pointer' : ''}`} onClick={showSkus ? () => toggleCollapsed(style.id) : undefined}>
          <td className={`${STICKY_CELL_CLASS} bg-white group-hover:bg-[#F9FAFB] px-3.5 py-2.5 transition-colors`}>
            <div className="flex items-center gap-2 min-w-0" style={{ width: `calc(${widthOf(PRODUCT_COLUMN_KEY)}px - 1.75rem)` }}>
              {showSkus && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCollapsed(style.id);
                  }}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${style.product_title}`}
                  className="p-0.5 text-[#98A2B3] hover:text-[#7F56D9] transition-colors shrink-0"
                >
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-[#7F56D9]" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              )}
              <span className="truncate min-w-0 text-[#101828] text-[13px] font-semibold leading-[20px]" title={style.product_title}>
                {style.product_title}
              </span>
              {countLabel && <span className="shrink-0 text-[12px] text-[#98A2B3] whitespace-nowrap">{countLabel}</span>}
              {style.never_sold && (
                <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-[#FFFAEB] text-[#B54708] border border-[#FEDF89] text-[11px] font-semibold whitespace-nowrap">
                  Never sold
                </span>
              )}
              {style.is_core === true && (
                <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-[#F4F0FF] text-[#7F56D9] border border-[#D6BBFB] text-[11px] font-semibold whitespace-nowrap">
                  Core
                </span>
              )}
              {style.is_reorder === true && (
                <span title="Continuous reorder item" aria-label="Continuous reorder item" className="shrink-0 inline-flex text-[#2563EB]">
                  <RotateCw className="w-3 h-3" />
                </span>
              )}
            </div>
          </td>
          {visibleOrderedColumns.map((col) => renderMetricCell(col, style, false))}
        </tr>

        {isOpen &&
          style.skus.map((sku) => (
            <tr key={sku.id} className="group bg-[#FAFAFB] hover:bg-[#F4F4F5] transition-colors">
              <td className={`${STICKY_CELL_CLASS} bg-[#FAFAFB] group-hover:bg-[#F4F4F5] pl-10 pr-4 py-2 text-[12px] text-[#64748B] whitespace-nowrap`}>
                <span className="font-mono">{sku.sku_id}</span>
              </td>
              {visibleOrderedColumns.map((col) => renderMetricCell(col, sku, true))}
            </tr>
          ))}
      </React.Fragment>
    );
  };

  const renderGroupRows = (group: AgedInventoryGroup) => {
    const groupStylesShown = group.styles.filter((style) => shownIds.has(style.id));
    if (groupStylesShown.length === 0) return null;
    const isOpen = !collapsedGroups[group.key];
    return (
      <React.Fragment key={`group-${group.key}`}>
        <tr
          className="group bg-[#F8F9FA] hover:bg-[#F2F4F7] cursor-pointer transition-colors font-semibold"
          onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group.key]: isOpen }))}
        >
          <td className={`${STICKY_CELL_CLASS} bg-[#F8F9FA] group-hover:bg-[#F2F4F7] px-3.5 py-2.5 whitespace-nowrap`}>
            <div className="flex items-center gap-2">
              {isOpen ? <ChevronDown className="w-4 h-4 text-[#344054]" /> : <ChevronRight className="w-4 h-4 text-[#344054]" />}
              <span className="text-[13px] font-bold text-[#101828] truncate">{group.label}</span>
              <span className="text-[12px] text-[#667085] font-normal">
                {stylesLabel(group.totals.styleCount)}
              </span>
            </div>
          </td>
          {renderTotalsCells(group.totals, 'font-semibold text-[#101828]')}
        </tr>
        {isOpen && groupStylesShown.map(renderStyleRows)}
      </React.Fragment>
    );
  };

  const colSpan = visibleOrderedColumns.length + 1;
  const hasActiveFilters = chips.length > 0;
  const searchConfig = SEARCH_FIELD_OPTIONS.find((opt) => opt.key === searchField) ?? SEARCH_FIELD_OPTIONS[0];

  return (
    <div className="w-full bg-[#FAFAFC] min-h-screen text-[#09090B] p-6 space-y-4 font-sans antialiased relative">
      {/* --- Breadcrumb & header --- */}
      <div className="flex flex-col gap-3">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] text-[#667085]">
          {onClose ? (
            <button type="button" onClick={onClose} className="hover:text-[#344054] cursor-pointer">
              Reports
            </button>
          ) : (
            <span>Reports</span>
          )}
          <ChevronRight className="w-3.5 h-3.5 text-[#98A2B3]" />
          <span>Vendor &amp; SKU</span>
          <ChevronRight className="w-3.5 h-3.5 text-[#98A2B3]" />
          <span aria-current="page" className="font-medium text-[#101828]">
            Aged Inventory
          </span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#F5F0FF] border border-[#E9D7FE] flex items-center justify-center shadow-2xs shrink-0">
              <Hourglass className="w-5 h-5 text-[#7C3AED]" />
            </div>
            <div>
              <h1 className="text-[22px] font-bold text-[#09090B] tracking-tight leading-tight">Aged Inventory</h1>
              <p className="text-[13px] text-[#71717A] mt-1 max-w-xl leading-normal">
                Every item currently on hand with how long it has been sitting. Aging is calculated from actual receive date — not when the
                product was created in your POS.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* How this report works */}
            <div className="relative" data-popover-container>
              <button
                type="button"
                onClick={() => togglePopover('how_it_works')}
                aria-expanded={activePopover === 'how_it_works'}
                className="h-[34px] px-2.5 rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F2F4F7] flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <CircleHelp className="w-4 h-4 text-[#667085]" />
                <span>How this report works</span>
              </button>
              {activePopover === 'how_it_works' && (
                <div
                  role="dialog"
                  aria-label="How this report works"
                  className="absolute right-0 top-11 z-[60] w-96 bg-white rounded-2xl shadow-xl border border-[#E4E4E7] p-4 space-y-2.5 text-[13px] text-[#475467] leading-relaxed animate-in fade-in zoom-in-95 duration-100"
                >
                  <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                    <h4 className="text-[13px] font-bold text-[#101828]">How this report works</h4>
                    <button type="button" onClick={closePopover} aria-label="Close" className="text-[#98A2B3] hover:text-[#344054] cursor-pointer">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p>Lists every style with units on hand at the latest inventory snapshot. Age counts the days since a SKU was first received, not when it was created in your POS.</p>
                  <p>A style takes the age of its oldest SKU; the range under the age shows its newest to oldest SKU. Styles without a receive date show a blank age and are listed last.</p>
                  <p>Units Sold and Sell-Through % (units sold / (units sold + units on hand)) cover the selected date range.</p>
                  <ul className="space-y-1">
                    {AGING_TIERS.map((tier) => (
                      <li key={tier.key} className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${tier.badgeClass}`}>{tier.name}</span>
                        <span>{tier.rangeLabel}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Saved views */}
            <div className="relative" data-popover-container>
              <button type="button" onClick={() => togglePopover('saved_views')} aria-expanded={activePopover === 'saved_views'} className={HEADER_BUTTON_CLASS}>
                <Bookmark className="w-4 h-4 text-[#667085]" />
                <span>Save view</span>
              </button>

              {activePopover === 'saved_views' && (
                <div className="absolute right-0 top-11 z-[60] w-72 bg-white rounded-2xl shadow-xl border border-[#E4E4E7] p-4 space-y-3 animate-in fade-in zoom-in-95 duration-100">
                  <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                    <h4 className="text-[13px] font-bold text-[#101828]">Saved Views</h4>
                    <button type="button" onClick={closePopover} aria-label="Close saved views" className="text-[#98A2B3] hover:text-[#344054] cursor-pointer">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {savedViews.length === 0 && <p className="text-[12px] text-[#98A2B3] italic px-1">No saved views yet.</p>}
                    {savedViews.map((sv) => (
                      <div
                        key={sv.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleApplySavedView(sv.payload)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleApplySavedView(sv.payload);
                        }}
                        className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-[#F9FAFB] cursor-pointer text-[13px] font-semibold text-[#344054] transition-colors"
                      >
                        <span className="flex flex-col min-w-0">
                          <span className="truncate">{sv.name}</span>
                          {sv.visibility === 'shared' && (
                            <span className="text-[11px] font-medium text-[#667085] flex items-center gap-1 truncate">
                              <Users className="w-3 h-3 shrink-0" />
                              {sv.canEdit || !sv.ownerName ? 'Shared' : `Shared by ${sv.ownerName}`}
                            </span>
                          )}
                        </span>
                        {sv.canEdit && (
                          <button
                            type="button"
                            onClick={(e) => handleDeleteSavedView(sv.id, sv.name, e)}
                            aria-label={`Delete saved view ${sv.name}`}
                            className="text-[#98A2B3] hover:text-[#B42318] cursor-pointer"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-[#EAECF0] pt-3 space-y-2">
                    <input
                      type="text"
                      placeholder="New view name..."
                      aria-label="New view name"
                      value={newViewName}
                      maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
                      onChange={(e) => setNewViewName(e.target.value)}
                      className="w-full px-3 py-1.5 text-[13px] border border-[#D0D5DD] rounded-[9px] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] text-[#344054]"
                    />
                    <div className="flex items-center gap-2" role="group" aria-label="Visibility">
                      {(
                        [
                          ['only_me', 'Only me', Lock],
                          ['shared', 'Shared', Users],
                        ] as const
                      ).map(([value, label, Icon]) => (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={newViewVisibility === value}
                          onClick={() => setNewViewVisibility(value)}
                          className={`flex-1 py-1.5 rounded-[9px] text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                            newViewVisibility === value
                              ? 'bg-[#F4EBFF] text-[#6941C6] border border-[#D6BBFB]'
                              : 'bg-white border border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                      ))}
                    </div>
                    {newViewVisibility === 'shared' && (
                      <p className="text-[11px] text-[#667085]">Everyone with access to this client can see and apply this view. Only you can delete it.</p>
                    )}
                    {savedViewError && (
                      <p role="alert" className="text-[12px] text-[#B42318]">
                        {savedViewError}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveCurrentView}
                      disabled={isSavingView || !newViewName.trim()}
                      className="w-full py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-semibold rounded-[9px] shadow-xs transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Save Current Config
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Export -- off until there are rows, so it can't save a header-only file. */}
            <button type="button" onClick={handleExportCSV} disabled={loading || orderedStyles.length === 0} className={HEADER_BUTTON_CLASS}>
              <Download className="w-4 h-4 text-[#667085]" />
              <span>Export CSV</span>
            </button>

            {/* Same placeholder as the other vendor reports; report favorites aren't stored yet. */}
            <button type="button" className={ICON_BUTTON_CLASS} title="Favorite" aria-label="Favorite">
              <Star className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* --- Filters & controls --- */}
      <div className="bg-white p-5 rounded-[16px] border border-[#E4E4E7] shadow-2xs relative z-30 flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <DateRangeFilter
            info={INFO.dateRange}
            presets={DATE_RANGE_PRESETS}
            customPreset={CUSTOM_DATE_RANGE}
            value={filters.dateRange}
            customStart={filters.customStartDate}
            customEnd={filters.customEndDate}
            isOpen={activePopover === 'date_range'}
            onToggle={() => togglePopover('date_range')}
            onPreset={(preset) => {
              updateFilters({ dateRange: preset, customStartDate: '', customEndDate: '' });
              closePopover();
            }}
            onCustom={(start, end) => {
              updateFilters({ dateRange: CUSTOM_DATE_RANGE, customStartDate: start, customEndDate: end });
              closePopover();
            }}
          />
          <MultiSelectFilter
            label="Location"
            allLabel="All locations"
            info={INFO.location}
            noun="location"
            pluralNoun="locations"
            options={locationOptions}
            appliedCodes={filters.locations}
            appliedExclude={filters.excludeLocations}
            draft={locationDraft}
            isOpen={activePopover === 'location'}
            onToggle={() => togglePopover('location')}
            onClose={closePopover}
          />
          <MultiSelectFilter
            label="Category"
            allLabel="All categories"
            info={INFO.category}
            noun="category"
            pluralNoun="categories"
            options={categoryOptions}
            appliedCodes={filters.categories}
            appliedExclude={filters.excludeCategories}
            draft={categoryDraft}
            isOpen={activePopover === 'category'}
            onToggle={() => togglePopover('category')}
            onClose={closePopover}
          />
          <MultiSelectFilter
            label="Class"
            allLabel="All classes"
            info={INFO.class}
            noun="class"
            pluralNoun="classes"
            options={classOptions}
            appliedCodes={filters.classes}
            appliedExclude={filters.excludeClasses}
            draft={classDraft}
            isOpen={activePopover === 'class'}
            onToggle={() => togglePopover('class')}
            onClose={closePopover}
            matches={(option, query) => matchesClassQuery(option, query)}
          />
          <MultiSelectFilter
            label="Vendor"
            allLabel="All vendors"
            info={INFO.vendor}
            noun="vendor"
            pluralNoun="vendors"
            options={vendorOptions}
            appliedCodes={filters.vendors}
            appliedExclude={filters.excludeVendors}
            draft={vendorDraft}
            isOpen={activePopover === 'vendor'}
            onToggle={() => togglePopover('vendor')}
            onClose={closePopover}
          />
          <SingleSelectFilter<number | null>
            label="Age threshold"
            info={INFO.ageThreshold}
            value={filters.ageThreshold}
            options={[{ value: null, label: ageThresholdLabel(null) }, ...AGE_THRESHOLD_OPTIONS.map((days) => ({ value: days, label: ageThresholdLabel(days) }))]}
            isOpen={activePopover === 'age_threshold'}
            onToggle={() => togglePopover('age_threshold')}
            onChange={(value) => {
              updateFilters({ ageThreshold: value });
              closePopover();
            }}
          />
          <SingleSelectFilter<AgingTierKey | null>
            label="Aging tier"
            info={INFO.agingTier}
            value={filters.agingTier}
            options={[{ value: null, label: 'All tiers' }, ...AGING_TIERS.map((tier) => ({ value: tier.key, label: agingTierLabel(tier) }))]}
            isOpen={activePopover === 'aging_tier'}
            onToggle={() => togglePopover('aging_tier')}
            onChange={(value) => {
              updateFilters({ agingTier: value });
              closePopover();
            }}
          />
        </div>

        <div className="border-t border-[#EAECF0]" />

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-4">
            <SingleSelectFilter<AgedInventoryHierarchy>
              label="Hierarchy"
              info={INFO.hierarchy}
              value={hierarchy}
              options={HIERARCHY_OPTIONS.map((opt) => ({ value: opt.key, label: opt.label, description: opt.description }))}
              menuWidthClass="w-72"
              isOpen={activePopover === 'hierarchy'}
              onToggle={() => togglePopover('hierarchy')}
              onChange={(value) => {
                setHierarchy(value);
                closePopover();
              }}
              className="min-w-[170px]"
            />
            <SegmentedControl<SkuGrain> label="Show SKUs" info={INFO.showSkus} value={skuGrain} options={SHOW_SKUS_OPTIONS} onChange={setSkuGrain} />
            <ToggleFilter label="Zero-sales only" info={INFO.zeroSales} checked={filters.zeroSalesOnly} onChange={(checked) => updateFilters({ zeroSalesOnly: checked })} />
            <ToggleFilter label="Exclude core/reorder" info={INFO.excludeCore} checked={filters.excludeCore} onChange={(checked) => updateFilters({ excludeCore: checked })} />
          </div>

          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <FilterLabel label="Search" info={INFO.search} />
              <div className="flex items-center gap-2">
                <SingleSelectFilter<SearchField>
                  label="Search by"
                  hideLabel
                  value={searchField}
                  options={SEARCH_FIELD_OPTIONS.map((opt) => ({ value: opt.key, label: opt.label }))}
                  isOpen={activePopover === 'search_field'}
                  onToggle={() => togglePopover('search_field')}
                  onChange={(value) => {
                    setSearchField(value);
                    closePopover();
                  }}
                  className="w-[140px]"
                />
                <SearchInput value={searchQuery} placeholder={searchConfig.placeholder} onChange={setSearchQuery} />
              </div>
            </div>

            {/* Column visibility */}
            <div className="relative" data-popover-container>
              <button
                type="button"
                onClick={() => togglePopover('columns')}
                aria-expanded={activePopover === 'columns'}
                className="h-[34px] px-3 bg-white border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <SlidersHorizontal className="w-4 h-4 text-[#667085]" />
                <span>Columns</span>
              </button>
              {activePopover === 'columns' && (
                <div
                  role="dialog"
                  aria-label="Visible columns"
                  className="absolute right-0 top-full mt-1.5 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3.5 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-100"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[#71717A] px-1 pb-1.5">Visible columns</div>
                  <div className="flex flex-col gap-0.5 max-h-[60vh] overflow-y-auto pr-1">
                    {AGED_INVENTORY_COLUMNS.map((col) => (
                      <label
                        key={col.key}
                        className="flex items-start gap-2.5 cursor-pointer hover:bg-[#F9FAFB] p-1 rounded-lg transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => setVisibleColumns((prev) => ({ ...prev, [col.key]: !prev[col.key] }))}
                          className="w-4 h-4 mt-0.5 accent-[#7F56D9] rounded border-[#D0D5DD] shrink-0"
                        />
                        <span className="flex flex-col min-w-0">
                          <span className="text-[13px] font-semibold text-[#344054] leading-[20px]">{col.label}</span>
                          <span className="text-[12px] text-[#71717A] leading-[16px]">
                            {col.helper}
                            {col.skuOnly && ' · SKU view'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="border-t border-[#EAECF0] mt-1.5 pt-2 px-1 text-[12px] text-[#71717A] leading-[16px]">{COLUMNS_POPOVER_NOTE}</p>
                </div>
              )}
            </div>

            <button type="button" onClick={handleResetAll} title="Reset filters and layout" aria-label="Reset filters and layout" className={ICON_BUTTON_CLASS}>
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <FilterChips chips={chips} onClearAll={clearFilters} />
      </div>

      {/* --- Summary --- */}
      <div className="flex items-center gap-1 text-[13px] text-[#71717A] px-1" aria-live="polite">
        {loading ? (
          <span>Loading&hellip;</span>
        ) : (
          <>
            <span>
              <strong className="text-[#09090B] font-semibold">
                {orderedStyles.length} of {stylesLabel(styles.length)}
              </strong>{' '}
              shown ({skuCount} sizes/colors total) &middot; sorted by <strong className="text-[#09090B] font-semibold">{sortKeyLabel(sort.key)}</strong>,{' '}
              {sortDirectionLabel(sort)}
            </span>
            <InfoTooltip text={INFO.summary} position="bottom" align="left" />
          </>
        )}
      </div>

      {loadError && (
        <div role="alert" className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {/* --- Table --- */}
      <div className="bg-white border border-[#E4E4E7] rounded-2xl shadow-2xs overflow-hidden relative z-10">
        <div className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
          <table className="w-full text-left text-[13px] border-collapse">
            <thead className="sticky top-0 z-20 bg-[#F9F9FB]">
              <tr className="bg-[#F9F9FB] border-b border-[#E4E4E7] text-[#64748B] font-semibold select-none">
                <th
                  style={cellStyle(PRODUCT_COLUMN_KEY)}
                  className={`sticky left-0 top-0 bg-[#F9F9FB] ${activeMenuColumn === PRODUCT_COLUMN_KEY ? 'z-50' : 'z-30'} px-3.5 py-2.5 border-r border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] text-left`}
                >
                  <HeaderButton
                    label={PRODUCT_COLUMN_LABEL}
                    description={PRODUCT_COLUMN_DESCRIPTION}
                    isActive={sort.key === PRODUCT_COLUMN_KEY}
                    direction={sort.direction}
                    isMenuOpen={activeMenuColumn === PRODUCT_COLUMN_KEY}
                    align="left"
                    tooltipAlign="left"
                    onToggleMenu={() => toggleColumnMenu(PRODUCT_COLUMN_KEY)}
                    // The sticky Product column can only be sorted -- no pin or hide.
                    menu={
                      <ColumnHeaderMenu
                        title={PRODUCT_COLUMN_LABEL}
                        description={PRODUCT_COLUMN_DESCRIPTION}
                        sortType="text"
                        activeDirection={sort.key === PRODUCT_COLUMN_KEY ? sort.direction : null}
                        onSort={(direction) => setSort({ key: PRODUCT_COLUMN_KEY, direction })}
                        onClose={closeColumnMenu}
                      />
                    }
                  />
                  <span {...getResizeHandleProps(PRODUCT_COLUMN_KEY, PRODUCT_COLUMN_LABEL)} className={COLUMN_RESIZE_HANDLE_CLASS} />
                </th>

                {visibleOrderedColumns.map((col, idx) => {
                  const isMenuOpen = activeMenuColumn === col.key;
                  const nearRightEdge = idx >= visibleOrderedColumns.length - 2;
                  const pinned = isPinned(col.key);
                  const sortKey: AgedInventorySortKey = col.key;
                  return (
                    <th
                      key={col.key}
                      {...getColumnDragProps(col.key)}
                      draggable={!isResizing && !pinned && !isMenuOpen}
                      style={cellStyle(col.key)}
                      className={`relative px-4 py-2.5 sticky top-0 bg-[#F9F9FB] ${isMenuOpen ? 'z-50' : 'z-20'} hover:z-50 ${ALIGN_CLASS[col.align]} ${pinned ? '' : 'cursor-grab'} ${dragClass(col.key, 'x')}`}
                    >
                      <HeaderButton
                        label={col.label}
                        description={col.description}
                        isActive={sort.key === sortKey}
                        direction={sort.direction}
                        isMenuOpen={isMenuOpen}
                        align={col.align}
                        tooltipAlign={nearRightEdge ? 'right' : 'left'}
                        onToggleMenu={() => toggleColumnMenu(col.key)}
                        menu={
                          <ColumnHeaderMenu
                            title={col.label}
                            description={col.description}
                            sortType={col.sortType}
                            activeDirection={sort.key === sortKey ? sort.direction : null}
                            onSort={(direction) => setSort({ key: sortKey, direction })}
                            pin={{ isPinned: pinned, onToggle: () => togglePinnedColumn(col.key) }}
                            onHide={() => setVisibleColumns((prev) => ({ ...prev, [col.key]: false }))}
                            onClose={closeColumnMenu}
                            align={nearRightEdge ? 'right' : 'left'}
                          />
                        }
                      />
                      <span {...getResizeHandleProps(col.key, col.label)} className={COLUMN_RESIZE_HANDLE_CLASS} />
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="divide-y divide-[#E4E4E7]">
              {loading ? (
                Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIdx) => (
                  <tr key={`skeleton-${rowIdx}`} data-testid="aged-inventory-skeleton-row">
                    <td className={`${STICKY_CELL_CLASS} bg-white px-3.5 py-3`}>
                      <Skeleton className="h-4 w-3/4 bg-[#F2F4F7]" />
                    </td>
                    {visibleOrderedColumns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <Skeleton className="h-4 w-full bg-[#F2F4F7]" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : orderedStyles.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-14 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-[#F2F4F7] flex items-center justify-center">
                        <Package className="w-5 h-5 text-[#98A2B3]" />
                      </div>
                      <p className="text-[14px] font-semibold text-[#101828]">
                        {hasActiveFilters ? 'No aged inventory matches these filters' : 'No on-hand inventory found'}
                      </p>
                      <p className="text-[13px] text-[#71717A]">
                        {hasActiveFilters
                          ? 'Try widening the age threshold or removing a filter.'
                          : 'Styles appear here once the latest inventory snapshot has units on hand.'}
                      </p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-1 h-[32px] px-3 bg-white border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs cursor-pointer"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  <tr className="bg-[#F2F4F7]" data-testid="aged-inventory-total-row">
                    <td className={`${STICKY_CELL_CLASS} bg-[#F2F4F7] px-3.5 py-2.5 whitespace-nowrap`}>
                      <span className="text-[13px] font-bold text-[#101828]">Total</span>{' '}
                      <span className="text-[12px] text-[#667085]">
                        {stylesLabel(totals.styleCount)}
                      </span>
                    </td>
                    {renderTotalsCells(totals, 'font-semibold text-[#101828]')}
                  </tr>
                  {hierarchy === 'total' ? shownStyles.map(renderStyleRows) : groups.map(renderGroupRows)}
                </>
              )}
            </tbody>
          </table>
        </div>

        {!loading && orderedStyles.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#E4E4E7] text-[13px] text-[#667085]">
            <span>
              Showing {shownStyles.length} of {stylesLabel(orderedStyles.length)}
            </span>
            {remaining > 0 && (
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="h-[32px] px-3 bg-white border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs cursor-pointer"
              >
                Show {Math.min(PAGE_SIZE, remaining)} more {Math.min(PAGE_SIZE, remaining) === 1 ? 'style' : 'styles'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* --- Footer note --- */}
      <div className="flex items-start gap-2 text-[12px] text-[#71717A] leading-relaxed">
        <Info className="w-4 h-4 text-[#98A2B3] shrink-0 mt-0.5" />
        <span>
          Units Sold, Sell-Through % and Zero-sales use the selected date range ({formatDateRange(from, to)}). Age and Aging tier are as of{' '}
          {asOfDate ? formatDate(asOfDate) : 'today'}. Styles with several sizes or colors roll up their sizes and,
          when sorted by age, rank by their oldest (worst-case) size. Switch Show SKUs to SKU for the full breakdown. Aging tier thresholds vary by
          vertical and are configurable per client.
        </span>
      </div>
    </div>
  );
};

export default AgedInventoryReport;
